import React, { useState } from 'react';
import { Button } from 'reactstrap';
import { OfferRequisition } from '../../../../../../entities/v1/applicant_tracking/OfferRequisition';
import { User } from '../../../../../../entities/User';
import {
  buildSteps,
  getApprovableStepOrders,
  getCurrentStepOrder,
} from '../../../../../../utils/applicant_tracking/requisition';
import { AlertMessage } from '../../../../../../components/AlertMessage';
import { DeclineModal } from './DeclineModal';
import { AlertObject, AlertType } from '../../../../../../components/Alert';
import { OfferRequisitionService } from '../../../../../../services/v1/applicant_tracking/OfferRequisitionService';

interface PropTypes {
  currentUser: User;
  offerRequisition: OfferRequisition;
  setLoadingState: (loading: boolean) => void;
  setAlertMessage: (message: AlertObject) => void;
  reloadOfferRequisition: () => void;
}

export function buildAndSetAlertMessage(
  setAlertMessage: (message: AlertObject) => void,
  action: string,
  offerRequisition: OfferRequisition,
  error: boolean,
) {
  const actionMessage = error
    ? `'could not be ${action}`
    : `was successfully ${action}`;

  const alertMessage = {
    type: error ? AlertType.Danger : AlertType.Success,
    message: (
      <>
        The offer requisition for{' '}
        <span className='fw-bold'>
          {offerRequisition.signableOffer.application.candidate.name}
        </span>{' '}
        {actionMessage}!
      </>
    ),
  };
  setAlertMessage(alertMessage);
}

function ApproverOrDeclineOptions(props: PropTypes) {
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const approveOfferRequisition = () => {
    props.setLoadingState(true);
    OfferRequisitionService.approve(props.offerRequisition.id)
      .then(() => {
        buildAndSetAlertMessage(
          props.setAlertMessage,
          'approved',
          props.offerRequisition,
          false,
        );
      })
      .catch(() => {
        buildAndSetAlertMessage(
          props.setAlertMessage,
          'approved',
          props.offerRequisition,
          true,
        );
      })
      .finally(() => {
        props.reloadOfferRequisition();
        props.setLoadingState(false);
      });
  };

  return (
    <div className='d-flex align-items-center'>
      <Button
        color='danger'
        className='me-3'
        onClick={() => setModalOpen(true)}
      >
        Decline
      </Button>
      <Button
        color='primary'
        className='me-3'
        onClick={approveOfferRequisition}
      >
        Approve
      </Button>
      {modalOpen && (
        <DeclineModal
          currentUser={props.currentUser}
          offerRequisition={props.offerRequisition}
          setLoadingState={props.setLoadingState}
          setAlertMessage={props.setAlertMessage}
          reloadOfferRequisition={props.reloadOfferRequisition}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}

function NoActionAlert(props: {
  approvableStepOrders: number[];
  currentStepOrder: number;
}) {
  const actionAtLaterStage = props.approvableStepOrders.some(
    (order) => order > props.currentStepOrder,
  );
  const message = actionAtLaterStage
    ? 'is still pending further approvals.'
    : 'has already been approved at this level.';

  return (
    <AlertMessage
      className='bg-light-warning fs-5'
      icon={{ name: 'bi-info-circle' }}
      text={`No action required${
        actionAtLaterStage ? ' yet' : ''
      }. This offer requisition ${message}`}
    />
  );
}

export function ApproverActions(props: PropTypes) {
  const approvalSteps = buildSteps(props.offerRequisition.approvers);
  const currentStepOrder = getCurrentStepOrder(approvalSteps);
  const approvableStepOrders = getApprovableStepOrders(
    props.offerRequisition,
    props.currentUser,
  );

  if (approvableStepOrders.includes(currentStepOrder)) {
    return <ApproverOrDeclineOptions {...props} />;
  }

  return (
    <NoActionAlert
      approvableStepOrders={approvableStepOrders}
      currentStepOrder={currentStepOrder}
    />
  );
}
